/**
 * Affiliate Attribution Tracker
 * Generates affiliate-tagged reservation links, records click events for
 * commission tracking, and calculates estimated commission.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 7.1
 */

import { OPENTABLE_CONFIG } from '../config/opentable';
import { API_BASE_URL } from '../config/environment';
import { authFetch } from '../utils/authFetch';

/**
 * Generate a UUID v4 string.
 * Uses crypto.randomUUID() when available, otherwise falls back to a
 * manual implementation.
 * @returns {string} UUID v4 string
 */
function generateUUIDv4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ── Average check amounts by price range ────────────────────────────────────
const AVERAGE_CHECK_AMOUNTS = {
  '$': 15,
  '$$': 30,
  '$$$': 50,
  '$$$$': 80,
};

/**
 * Generate an affiliate-tagged reservation link.
 *
 * Appends the TravelFlow affiliate partner ID and a unique UUID v4 click
 * identifier as query parameters to the reservation URL.
 *
 * When the affiliate partner ID is not configured (empty string), the link
 * is returned without affiliate parameters and a warning is logged.
 *
 * @param {string} reservationUrl - Base reservation URL from OpenTable
 * @param {Object} context
 * @param {string} context.userId - Cognito user ID
 * @param {string} context.tripId - Associated trip ID
 * @param {string} context.restaurantId - OpenTable restaurant ID
 * @returns {string} Affiliate-tagged reservation URL
 *
 * Requirements: 4.1, 4.2, 4.4, 4.5
 */
export function generateAffiliateLink(reservationUrl, { userId: _userId, tripId: _tripId, restaurantId: _restaurantId } = {}) {
  // Validate URL scheme and origin to prevent open-redirect / script injection
  // from untrusted data (AI-generated recommendations, mock responses, etc.)
  const ALLOWED_ORIGINS = [
    'www.opentable.com',
    'opentable.com',
    'restaurant.opentable.com',
  ];

  try {
    const parsed = new URL(reservationUrl);
    if (parsed.protocol !== 'https:' || !ALLOWED_ORIGINS.some(o => parsed.hostname === o || parsed.hostname.endsWith('.' + o))) {
      console.warn(`[AffiliateService] Rejected reservation URL with disallowed origin: ${parsed.hostname}`);
      return '';
    }
  } catch {
    console.warn('[AffiliateService] Rejected malformed reservation URL');
    return '';
  }

  const partnerId = OPENTABLE_CONFIG.affiliate.partnerId;

  if (!partnerId) {
    console.warn('[AffiliateService] Affiliate partner ID is not configured — generating link without affiliate params');
    return reservationUrl;
  }

  const clickId = generateUUIDv4();
  const url = new URL(reservationUrl);
  url.searchParams.set('ref', partnerId);
  url.searchParams.set('clickId', clickId);

  return url.toString();
}

/**
 * Record a reservation click event to the analytics endpoint.
 *
 * Uses a fire-and-forget pattern: the call is non-blocking and failures
 * are logged as warnings without propagating.
 *
 * @param {Object} params
 * @param {string} params.userId - Cognito user ID
 * @param {string} params.tripId - Associated trip ID
 * @param {string} params.restaurantId - OpenTable restaurant ID
 * @param {string} params.restaurantName - Restaurant name
 * @param {string} params.destinationCity - City of the restaurant
 * @param {string} params.linkUrl - The affiliate link URL
 * @returns {Promise<void>}
 *
 * Requirements: 4.3, 7.1
 */
export async function trackReservationClick({ userId, tripId, restaurantId, restaurantName, destinationCity, linkUrl }) {
  try {
    await authFetch(`${API_BASE_URL}/opentable-booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType: 'reservation_click',
        userId,
        tripId,
        restaurantId,
        restaurantName,
        destinationCity,
        linkUrl,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch (error) {
    console.warn('[AffiliateService] Failed to track reservation click:', error.message);
  }
}

/**
 * Calculate estimated commission based on average check amount for a
 * price range multiplied by the commission rate.
 *
 * Price range mapping:
 *   '$'    → $15 average check
 *   '$$'   → $30 average check
 *   '$$$'  → $50 average check
 *   '$$$$' → $80 average check
 *
 * @param {string} priceRange - One of '$', '$$', '$$$', '$$$$'
 * @param {number} commissionRate - Commission rate (e.g. 0.04 for 4%)
 * @returns {number} Estimated commission rounded to 2 decimal places, non-negative
 *
 * Requirements: 7.4
 */
export function calculateCommission(priceRange, commissionRate) {
  const averageCheck = AVERAGE_CHECK_AMOUNTS[priceRange] || 0;
  const commission = averageCheck * commissionRate;
  return Math.max(0, Math.round(commission * 100) / 100);
}

export default {
  generateAffiliateLink,
  trackReservationClick,
  calculateCommission,
};
